/**
 * /admin/tickets/:ticketId — one support ticket (Administrator).
 *
 * Shows the full question, who asked it and when, the chat and collections it
 * came from, whether the support-mailbox email went out (ADR-0031), and the
 * status form: a new status, an optional resolution note, and a mandatory
 * reason. The change is audited server-side (`ticket.status_changed`) and the
 * prior state versioned.
 */

import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  getTicket,
  TICKET_STATUSES,
  updateTicket,
  type TicketOut,
  type TicketStatus,
} from "@/shared/api/tickets";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { askedBy, emailStatusLabel, formatUtc, STATUS_LABEL } from "./ticketFormat";
import { TicketStatusBadge } from "./ticketStatus";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <div>{children}</div>
    </Stack>
  );
}

export function TicketDetail() {
  const { ticketId = "" } = useParams();
  const getToken = useAccessToken();
  const [ticket, setTicket] = useState<TicketOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<TicketStatus>("open");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = (t: TicketOut) => {
    setTicket(t);
    setStatus(t.status);
    setNote(t.resolution_note ?? "");
  };

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      apply(await getTicket(token, ticketId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [getToken, ticketId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Alert variant="light" color="red" title="Could not load the ticket">
        {error}
      </Alert>
    );
  }
  if (!ticket) return <Loader />;

  const noteChanged = note !== (ticket.resolution_note ?? "");
  const changed = status !== ticket.status || noteChanged;
  const canSave = changed && reason.trim().length >= 3 && !saving;

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await updateTicket(token, ticket.ticket_id, {
        status,
        resolution_note: noteChanged ? note : null,
        reason: reason.trim(),
      });
      apply(updated);
      setReason("");
      toastSuccess(
        `Ticket ${updated.ticket_number} updated`,
        `Status: ${STATUS_LABEL[updated.status]}.`,
      );
    } catch (err) {
      toastApiError("Could not update the ticket", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="md" maw={900}>
      <Anchor component={RouterLink} to="/admin/tickets" size="sm">
        <Group gap={4}>
          <IconArrowLeft size={14} /> All tickets
        </Group>
      </Anchor>
      <Group gap="sm" align="center">
        <Title order={2} ff="monospace">
          {ticket.ticket_number}
        </Title>
        <TicketStatusBadge status={ticket.status} />
      </Group>

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <Field label={ticket.veeva_change_id ? "Request" : "Question"}>
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }} data-testid="ticket-question">
              {ticket.question}
            </Text>
          </Field>
          {ticket.veeva_change_id && (
            <Button
              component={RouterLink}
              to={`/admin/approvals/veeva/${ticket.veeva_change_id}`}
              variant="light"
              style={{ alignSelf: "flex-start" }}
              data-testid="ticket-open-approval"
            >
              Open the approval (Approve / Reject)
            </Button>
          )}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Field label="Asked by">
              <Text size="sm">
                {askedBy(ticket)}
                {ticket.created_by_email && ticket.created_by_display_name
                  ? ` <${ticket.created_by_email}>`
                  : ""}
              </Text>
            </Field>
            <Field label="Raised">
              <Text size="sm">{formatUtc(ticket.created_at_utc)}</Text>
            </Field>
            {ticket.chat_id && (
              <Field label="Chat">
                <Anchor
                  component={RouterLink}
                  to={`${
                    ticket.source === "explore"
                      ? "/solutions/explore"
                      : ticket.source === "veeva_user_change"
                        ? "/solutions/validation/second-line-support"
                        : "/solutions/validation/first-line-support"
                  }/${ticket.chat_id}`}
                  size="sm"
                >
                  {ticket.chat_name ?? ticket.chat_id}
                </Anchor>
                <Text size="xs" c="dimmed">
                  Opens only for the chat&apos;s owner or people it is shared with.
                </Text>
              </Field>
            )}
            <Field label="Collections">
              <Text size="sm">
                {ticket.collections.map((c) => c.name ?? c.index_id).join(", ") || "—"}
              </Text>
            </Field>
            <Field label="Email to support">
              <Text size="sm" data-testid="ticket-email-status">
                {emailStatusLabel(ticket.email_status)}
              </Text>
              {ticket.email_error && (
                <Text size="xs" c="dimmed">
                  {ticket.email_error}
                </Text>
              )}
            </Field>
            <Field label="Assigned to">
              <Text size="sm">{ticket.assigned_to_display_name ?? "—"}</Text>
            </Field>
          </SimpleGrid>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Stack gap="sm">
          <Title order={4}>Update status</Title>
          <Select
            label="Status"
            value={status}
            onChange={(v) => v && setStatus(v as TicketStatus)}
            data={TICKET_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
            allowDeselect={false}
            w={240}
            data-testid="ticket-status-select"
          />
          <Textarea
            label="Resolution note"
            description="What was done — visible to administrators."
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            autosize
            minRows={3}
            maxLength={4000}
          />
          <TextInput
            label="Reason"
            description="Recorded in the audit trail."
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            maxLength={200}
            required
          />
          <Group>
            <Button onClick={() => void onSave()} disabled={!canSave} loading={saving}>
              Save
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
